import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./MessagesPage.module.css";
import NavBar from "../../components/NavBar/NavBar";
import { BASE_URL } from "../../App";

// Define the timeout duration in milliseconds
const ERROR_TIMEOUT_MS = 5000;

export default function MessagesPage() {
    const navigate = useNavigate();
    const [threads, setThreads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    // Function to clear the error state
    const clearErr = useCallback(() => {
        setErr("");
    }, []);

    // 1. Effect for fetching data
    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                // teammate version had: `${BASE_URL}/backend/...`
                // your version had: `${BASE_URL}backend/...`
                // --- correct merged version ---
                const res = await fetch(`${BASE_URL}/backend/messages/list_conversations.php`, {
                    credentials: "include",
                });

                const data = await res.json();
                if (!alive) return;
                if (!res.ok || data.error) throw new Error("Failed to load conversations. Please try again later");

                setThreads(data.conversations || []);
                // Clear any previous error on success
                clearErr();
            } catch (e) {
                // Set the error message on failure
                setErr("Invalid session. Please login again");
            } finally {
                if (alive) setLoading(false);
            }
        })();

        return () => { alive = false; };
    }, [clearErr]); // Added clearErr to dependency array

    // 2. Effect for clearing the error after a timeout
    useEffect(() => {
        let timer;
        if (err) {
            // Set a timer to clear the error after the defined duration
            timer = setTimeout(clearErr, ERROR_TIMEOUT_MS);
        }

        // Cleanup function: Clear the timeout if the component unmounts
        // or if 'err' changes before the timeout fires (e.g., successful fetch)
        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [err, clearErr]); // Run this effect whenever 'err' or 'clearErr' changes

    return (
        <div className={styles.pageWrap}>
            <NavBar />

            <main className={styles.page}>
                <section className={styles.card}>
                    <h1 className={styles.title}>Messages</h1>

                    {loading && <div className={styles.loading}>Loading…</div>}
                    {/*The error message will now disappear after 5 seconds*/}
                    {err && <div className={styles.alertError} style={{ color: "#c33" }}>{err}</div>}

                    {!loading && !err && (
                        <ul className={styles.list}>
                            {threads.map((t) => {
                                const time = t.last_message_time
                                    ? new Date(t.last_message_time).toLocaleString()
                                    : "";

                                return (
                                    <li
                                        key={t.conversation_id}
                                        className={`${styles.row} ${t.is_unread ? styles.unread : styles.read}`}
                                        onClick={() => navigate(`/messages/${t.conversation_id}`)}
                                    >
                                        {t.is_unread && <span className={styles.unreadDot} />}

                                        <div className={styles.rowMain}>
                                            {/* 🔥 YOUR USERNAME CHANGE */}
                                            <div className={styles.nameTime}>
                                                <span className={styles.name}>
                                                    @{t.other_user?.username || "unknown"}
                                                </span>
                                                <span className={styles.time}>{time}</span>
                                            </div>

                                            <div className={styles.preview}>
                                                {t.last_message_text || "(no messages yet)"}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}

                            {threads.length === 0 && (
                                <li className={styles.empty}>No conversations yet.</li>
                            )}
                        </ul>
                    )}
                </section>
            </main>
        </div>
    );
}